import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/utils/api";
import { shouldShowSessionWarning } from "@/utils/sessionWarning";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [remaining, setRemaining] = useState(null);
  const [warnVisible, setWarnVisible] = useState(false);
  const warnedExpiry = useRef(null);
  const activityAt = useRef(0);
  const sentAt = useRef(0);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      if (e.response?.status !== 401) {
        console.error("Auth check error:", e);
      }
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    const now = Date.now();
    activityAt.current = now;
    sentAt.current = now;
    setUser(data);
    setWarnVisible(false);
    warnedExpiry.current = null;
    try { localStorage.setItem("parts_hub_auth_change", String(Date.now())); } catch {}
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout errors are non-critical — the client session is cleared regardless
      if (e?.response?.status !== 401) {
        console.error("Logout request failed:", e?.message);
      }
    }
    setUser(null);
    activityAt.current = 0;
    sentAt.current = 0;
    setWarnVisible(false);
    warnedExpiry.current = null;
    try { localStorage.setItem("parts_hub_auth_change", String(Date.now())); } catch {}
  };

  useEffect(() => {
    const changed = (event) => { if (event.key === "parts_hub_auth_change") checkAuth(); };
    window.addEventListener("storage", changed);
    return () => window.removeEventListener("storage", changed);
  }, [checkAuth]);

  useEffect(() => {
    if (!user?.id) {
      activityAt.current = 0;
      sentAt.current = 0;
      setRemaining(null);
      setWarnVisible(false);
      warnedExpiry.current = null;
      return;
    }
    const sessionStartedAt = Date.now();
    activityAt.current = sessionStartedAt;
    sentAt.current = sessionStartedAt;
    let active = true;
    const touched = () => { activityAt.current = Date.now(); };
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach(name => window.addEventListener(name, touched, { passive: true }));
    const timer = setInterval(async () => {
      try {
        const now = Date.now();
        const endpoint = activityAt.current > sentAt.current && now - sentAt.current >= 60000 ? "/auth/activity" : "/auth/me";
        const { data } = endpoint.endsWith("activity") ? await api.post(endpoint) : await api.get(endpoint);
        if (!active) return;
        if (endpoint.endsWith("activity")) sentAt.current = now;
        if (data.id && data.id !== user.id) { setUser(data); return; }
        const expiresAt = Number(data.idle_expires_at || 0);
        const secs = Math.ceil(expiresAt - Date.now() / 1000);
        setRemaining(secs);
        // Her yenilenen bitiş zamanı için en fazla bir uyarı göster. Böylece
        // kullanıcı devam ettikten 55 dakika sonra yeniden uyarılabilir.
        if (shouldShowSessionWarning(expiresAt, Date.now() / 1000, warnedExpiry.current)) {
          warnedExpiry.current = expiresAt;
          setWarnVisible(true);
        }
      } catch { /* API interceptor reports expired sessions. */ }
    }, 15000);
    return () => { active = false; clearInterval(timer); events.forEach(name => window.removeEventListener(name, touched)); };
  }, [user?.id]);

  const continueSession = async () => {
    try {
      const { data } = await api.post("/auth/activity");
      sentAt.current = Date.now();
      setRemaining(Math.ceil(data.idle_expires_at - Date.now() / 1000));
    } catch { /* Expiration is handled by the API interceptor; keep the warning on network failure. */ }
    setWarnVisible(false);
  };

  const dismissWarning = () => setWarnVisible(false);

  return (
    <AuthContext.Provider value={{ user, login, logout, checkAuth }}>
      {children}
      {warnVisible && user && <div role="alertdialog" aria-modal="true" aria-label="Oturum süresi uyarısı" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
          <h2 className="font-bold text-slate-800">Oturumunuz sona ermek üzere</h2>
          <p className="mt-2 text-sm text-slate-500">Yaklaşık {Math.max(0, remaining)} saniye kaldı. Çalışmaya devam edebilir veya çıkış yapabilirsiniz. Sepetiniz korunur. 12 saatlik azami süre dolduğunda yeniden giriş gerekir.</p>
          <div className="mt-5 flex gap-3"><button onClick={continueSession} className="flex-1 rounded-xl bg-[#0D1B3E] px-4 py-2 text-sm font-semibold text-white">Devam et</button><button onClick={logout} className="rounded-xl border border-slate-200 px-4 py-2 text-sm">Çıkış yap</button><button onClick={dismissWarning} aria-label="Kapat" className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-400">✕</button></div>
        </div>
      </div>}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
